import { usePartnersPageContent } from '@/features/partners/hooks/usePartnersPage';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/Skeleton';

interface HeroSectionProps {
  onCtaClick?: () => void;
}

export function HeroSection({ onCtaClick }: HeroSectionProps) {
  const { partnerPageContent, isLoading } = usePartnersPageContent();
  const hero = partnerPageContent.hero;

  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick();
    } else {
      const element = document.querySelector('#inquire');
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (isLoading) {
    return <HeroSkeleton />;
  }

  const title = hero.title;
  const eyebrow = hero.eyebrow;
  const description = hero.description;
  const buttonText = 'Start Your Partnership';

  return (
    <section
      className="relative min-h-[500px] md:min-h-[600px] flex items-center justify-center overflow-hidden"
      aria-labelledby="hero-title"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary to-primary" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center text-white">
        <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-white/75">
          {eyebrow}
        </p>
        <h1
          id="hero-title"
          className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
        >
          {title}
        </h1>

        <p className="text-lg md:text-xl lg:text-2xl mb-8 max-w-3xl mx-auto opacity-90">
          {description}
        </p>

        <Button
          size="lg"
          variant="secondary"
          onClick={handleCtaClick}
          className="text-lg px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-shadow h-auto"
        >
          {buttonText}
        </Button>
      </div>

      {/* Decorative bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
}

function HeroSkeleton() {
  return (
    <section className="relative min-h-[500px] md:min-h-[600px] flex items-center justify-center bg-muted">
      <div className="container mx-auto px-4 text-center">
        <Skeleton className="h-12 md:h-16 w-3/4 mx-auto mb-6" />
        <Skeleton className="h-6 md:h-8 w-2/3 mx-auto mb-4" />
        <Skeleton className="h-6 md:h-8 w-1/2 mx-auto mb-8" />
        <Skeleton className="h-14 w-48 mx-auto rounded-lg" />
      </div>
    </section>
  );
}

export default HeroSection;
